import { useState, useEffect, useCallback, useRef } from "react";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Switch } from "@/components/ui/switch";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Dialog as InnerDialog, DialogContent as InnerDialogContent,
  DialogHeader as InnerDialogHeader, DialogTitle as InnerDialogTitle,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/hooks/use-toast";
import { authFetch } from "@/lib/authFetch";
import {
  Lock, Unlock, Eye, EyeOff, Copy, Plus, Pencil, Trash2, ExternalLink,
  ShieldCheck, Search, X as XIcon, Settings2, Tag, Upload, Globe,
  Euro, Calendar, User, Phone, Mail,
} from "lucide-react";

const VAULT_KEY = "vault_session";
const VAULT_EXPIRY_KEY = "vault_expiry";
const SESSION_DURATION = 15 * 60 * 1000;

const RECURRENCE_OPTIONS = ["mensuel", "annuel", "unique", "aucune"];
const RECURRENCE_LABELS: Record<string, string> = {
  mensuel: "Mensuel", annuel: "Annuel", unique: "Unique", aucune: "Aucune",
};
const STATUSES = ["actif", "inactif", "a_resilier"];
const STATUS_LABELS: Record<string, string> = { actif: "Actif", inactif: "Inactif", a_resilier: "À résilier" };
const STATUS_COLORS: Record<string, string> = {
  actif: "bg-green-500", inactif: "bg-gray-400", a_resilier: "bg-red-500",
};

const AVATAR_COLORS = [
  "bg-blue-500", "bg-green-500", "bg-violet-500", "bg-rose-500",
  "bg-amber-500", "bg-teal-500", "bg-indigo-500", "bg-orange-500",
];

function getAvatarBg(name: string) {
  let h = 0;
  for (let i = 0; i < name.length; i++) h = name.charCodeAt(i) + ((h << 5) - h);
  return AVATAR_COLORS[Math.abs(h) % AVATAR_COLORS.length];
}

type VaultCategory = { id: string; name: string };

type VaultEntry = {
  id: string;
  product_name: string;
  provider_name?: string;
  site_url?: string;
  category?: string;
  description?: string;
  logo_url?: string;
  entry_type?: string;
  login?: string;
  password?: string;
  contract_name?: string;
  has_subscription?: boolean;
  recurrence?: string;
  price_monthly?: string;
  price_annual?: string;
  amount_paid?: string;
  subscription_date?: string;
  renewal_date?: string;
  commercial_contact?: string;
  phone?: string;
  email?: string;
  comment?: string;
  status?: string;
};

type FormData = {
  productName: string;
  providerName: string;
  siteUrl: string;
  category: string;
  description: string;
  logoUrl: string;
  entryType: string;
  login: string;
  password: string;
  contractName: string;
  hasSubscription: boolean;
  recurrence: string;
  priceMonthly: string;
  priceAnnual: string;
  amountPaid: string;
  subscriptionDate: string;
  renewalDate: string;
  commercialContact: string;
  phone: string;
  email: string;
  comment: string;
  status: string;
};

const EMPTY_FORM: FormData = {
  productName: "", providerName: "", siteUrl: "", category: "", description: "",
  logoUrl: "", entryType: "application", login: "", password: "",
  contractName: "", hasSubscription: false, recurrence: "", priceMonthly: "",
  priceAnnual: "", amountPaid: "", subscriptionDate: "", renewalDate: "",
  commercialContact: "", phone: "", email: "", comment: "", status: "actif",
};

function getVaultToken(): string | null {
  try {
    const token = sessionStorage.getItem(VAULT_KEY);
    const expiry = sessionStorage.getItem(VAULT_EXPIRY_KEY);
    if (!token || !expiry) return null;
    if (Date.now() > parseInt(expiry)) {
      sessionStorage.removeItem(VAULT_KEY);
      sessionStorage.removeItem(VAULT_EXPIRY_KEY);
      return null;
    }
    return token;
  } catch { return null; }
}

function saveVaultToken(token: string) {
  try {
    sessionStorage.setItem(VAULT_KEY, token);
    sessionStorage.setItem(VAULT_EXPIRY_KEY, String(Date.now() + SESSION_DURATION));
  } catch {}
}

function clearVaultToken() {
  try {
    sessionStorage.removeItem(VAULT_KEY);
    sessionStorage.removeItem(VAULT_EXPIRY_KEY);
  } catch {}
}

function getRemainingMinutes(): number {
  try {
    const expiry = sessionStorage.getItem(VAULT_EXPIRY_KEY);
    if (!expiry) return 0;
    return Math.max(0, Math.ceil((parseInt(expiry) - Date.now()) / 60000));
  } catch { return 0; }
}

async function vaultFetch(url: string, options: RequestInit = {}, vaultToken: string): Promise<Response> {
  return authFetch(url, {
    ...options,
    headers: { ...(options.headers || {}), "X-Vault-Token": vaultToken },
  });
}

function fmt(v?: string | null, decimals = 2): string {
  if (!v && v !== "0") return "—";
  const n = parseFloat(v || "0");
  return `${n.toFixed(decimals)} €`;
}

interface VaultModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export function VaultModal({ isOpen, onClose }: VaultModalProps) {
  const { toast } = useToast();
  const [vaultToken, setVaultToken] = useState<string | null>(() => getVaultToken());
  const [pinDigits, setPinDigits] = useState<string[]>(["", "", "", "", "", ""]);
  const [pinError, setPinError] = useState("");
  const [pinLoading, setPinLoading] = useState(false);
  const [pinConfigured, setPinConfigured] = useState<boolean | null>(null);
  const [setupMode, setSetupMode] = useState(false);
  const [remainingMin, setRemainingMin] = useState(getRemainingMinutes());
  const [entries, setEntries] = useState<VaultEntry[]>([]);
  const [categories, setCategories] = useState<VaultCategory[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [filterCategory, setFilterCategory] = useState("__all__");
  const [filterType, setFilterType] = useState("__all__");
  const [revealedPasswords, setRevealedPasswords] = useState<Set<string>>(new Set());
  const [showFormDialog, setShowFormDialog] = useState(false);
  const [showDetailDialog, setShowDetailDialog] = useState(false);
  const [showManageCategories, setShowManageCategories] = useState(false);
  const [editingEntry, setEditingEntry] = useState<VaultEntry | null>(null);
  const [detailEntry, setDetailEntry] = useState<VaultEntry | null>(null);
  const [formData, setFormData] = useState<FormData>(EMPTY_FORM);
  const [formLoading, setFormLoading] = useState(false);
  const [logoUploading, setLogoUploading] = useState(false);
  const [newCategoryName, setNewCategoryName] = useState("");
  const [categoryLoading, setCategoryLoading] = useState(false);
  const pinInputRefs = useRef<(HTMLInputElement | null)[]>([]);
  const logoInputRef = useRef<HTMLInputElement | null>(null);

  const isUnlocked = vaultToken !== null;

  useEffect(() => {
    if (!isUnlocked) return;
    const interval = setInterval(() => {
      const rem = getRemainingMinutes();
      setRemainingMin(rem);
      if (rem === 0) {
        clearVaultToken();
        setVaultToken(null);
        toast({ title: "Coffre-fort verrouillé", description: "Session expirée après 15 min." });
      }
    }, 10000);
    return () => clearInterval(interval);
  }, [isUnlocked]);

  const loadCategories = useCallback(async () => {
    try {
      const r = await authFetch("/api/vault/categories");
      if (r.ok) setCategories(await r.json());
    } catch {}
  }, []);

  useEffect(() => {
    if (!isOpen) return;
    loadCategories();
    if (!isUnlocked) {
      authFetch("/api/vault/status").then(r => r.json()).then(d => {
        setPinConfigured(d.configured);
        setSetupMode(!d.configured);
      }).catch(() => setPinConfigured(null));
    } else {
      loadEntries();
    }
  }, [isOpen, isUnlocked]);

  const loadEntries = useCallback(async () => {
    const token = getVaultToken();
    if (!token) return;
    setLoading(true);
    try {
      const r = await vaultFetch("/api/vault/entries", {}, token);
      if (r.ok) setEntries(await r.json());
    } finally { setLoading(false); }
  }, []);

  const handlePinInput = (index: number, value: string) => {
    if (!/^\d*$/.test(value)) return;
    const next = [...pinDigits];
    next[index] = value.slice(-1);
    setPinDigits(next);
    if (value && index < 5) pinInputRefs.current[index + 1]?.focus();
    if (next.every(d => d !== "") && index === 5) handleVerifyPin(next.join(""));
  };

  const handlePinKeyDown = (index: number, e: React.KeyboardEvent) => {
    if (e.key === "Backspace" && !pinDigits[index] && index > 0) pinInputRefs.current[index - 1]?.focus();
    if (e.key === "Enter" && pinDigits.join("").length === 6) handleVerifyPin(pinDigits.join(""));
  };

  const handleVerifyPin = async (pin: string) => {
    setPinError("");
    setPinLoading(true);
    try {
      const endpoint = setupMode ? "/api/vault/setup-pin" : "/api/vault/verify-pin";
      const r = await authFetch(endpoint, {
        method: "POST", body: JSON.stringify({ pin }),
        headers: { "Content-Type": "application/json" },
      });
      const data = await r.json();
      if (!r.ok) { setPinError(data.message || "Erreur"); setPinDigits(["", "", "", "", "", ""]); pinInputRefs.current[0]?.focus(); return; }
      if (setupMode) {
        toast({ title: "PIN configuré avec succès" });
        setPinConfigured(true); setSetupMode(false); setPinDigits(["", "", "", "", "", ""]);
        return;
      }
      saveVaultToken(data.vaultToken);
      setVaultToken(data.vaultToken);
      setRemainingMin(15);
      loadEntries();
    } catch { setPinError("Erreur réseau"); } finally { setPinLoading(false); }
  };

  const handleLock = () => {
    clearVaultToken(); setVaultToken(null);
    setPinDigits(["", "", "", "", "", ""]); setEntries([]);
  };

  const toggleReveal = (id: string) => {
    setRevealedPasswords(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const copyText = async (text: string, label = "Copié") => {
    await navigator.clipboard.writeText(text);
    toast({ title: label, description: "Copié dans le presse-papier." });
  };

  const handleLogoUpload = async (file: File) => {
    setLogoUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await authFetch("/api/vault/upload-logo", { method: "POST", body: fd });
      if (!r.ok) { toast({ title: "Erreur upload logo", variant: "destructive" }); return; }
      const { url } = await r.json();
      setFormData(p => ({ ...p, logoUrl: url }));
    } finally { setLogoUploading(false); }
  };

  const openCreate = () => {
    setEditingEntry(null);
    setFormData(EMPTY_FORM);
    setShowFormDialog(true);
  };

  const openEdit = (entry: VaultEntry) => {
    setEditingEntry(entry);
    setFormData({
      productName: entry.product_name || "", providerName: entry.provider_name || "",
      siteUrl: entry.site_url || "", category: entry.category || "",
      description: entry.description || "", logoUrl: entry.logo_url || "",
      entryType: entry.entry_type || "application", login: entry.login || "",
      password: "", contractName: entry.contract_name || "",
      hasSubscription: entry.has_subscription ?? false, recurrence: entry.recurrence || "",
      priceMonthly: entry.price_monthly || "", priceAnnual: entry.price_annual || "",
      amountPaid: entry.amount_paid || "", subscriptionDate: entry.subscription_date || "",
      renewalDate: entry.renewal_date || "", commercialContact: entry.commercial_contact || "",
      phone: entry.phone || "", email: entry.email || "",
      comment: entry.comment || "", status: entry.status || "actif",
    });
    setShowFormDialog(true);
  };

  const handleSave = async () => {
    if (!vaultToken || !formData.productName) return;
    setFormLoading(true);
    try {
      const body: Record<string, unknown> = {
        productName: formData.productName, providerName: formData.providerName || null,
        siteUrl: formData.siteUrl || null, category: formData.category || null,
        description: formData.description || null, logoUrl: formData.logoUrl || null,
        entryType: formData.entryType || "application", login: formData.login || null,
        contractName: formData.contractName || null, hasSubscription: formData.hasSubscription,
        recurrence: formData.recurrence || null,
        priceMonthly: formData.priceMonthly ? parseFloat(formData.priceMonthly) : null,
        priceAnnual: formData.priceAnnual ? parseFloat(formData.priceAnnual) : null,
        amountPaid: formData.amountPaid ? parseFloat(formData.amountPaid) : null,
        subscriptionDate: formData.subscriptionDate || null, renewalDate: formData.renewalDate || null,
        commercialContact: formData.commercialContact || null, phone: formData.phone || null,
        email: formData.email || null, comment: formData.comment || null,
        status: formData.status || "actif",
      };
      if (formData.password !== undefined) body.password = formData.password || undefined;
      const url = editingEntry ? `/api/vault/entries/${editingEntry.id}` : "/api/vault/entries";
      const method = editingEntry ? "PUT" : "POST";
      const r = await vaultFetch(url, {
        method, body: JSON.stringify(body), headers: { "Content-Type": "application/json" },
      }, vaultToken);
      if (!r.ok) { const d = await r.json(); toast({ title: "Erreur", description: d.message, variant: "destructive" }); return; }
      toast({ title: editingEntry ? "Entrée mise à jour" : "Entrée créée" });
      setShowFormDialog(false);
      loadEntries();
    } finally { setFormLoading(false); }
  };

  const handleDelete = async (id: string) => {
    if (!vaultToken || !confirm("Supprimer cette entrée ?")) return;
    const r = await vaultFetch(`/api/vault/entries/${id}`, { method: "DELETE" }, vaultToken);
    if (r.ok) { toast({ title: "Entrée supprimée" }); loadEntries(); }
  };

  const handleAddCategory = async () => {
    if (!vaultToken || !newCategoryName.trim()) return;
    setCategoryLoading(true);
    try {
      const r = await vaultFetch("/api/vault/categories", {
        method: "POST", body: JSON.stringify({ name: newCategoryName.trim() }),
        headers: { "Content-Type": "application/json" },
      }, vaultToken);
      const data = await r.json();
      if (!r.ok) { toast({ title: "Erreur", description: data.message, variant: "destructive" }); return; }
      setNewCategoryName(""); await loadCategories();
      toast({ title: "Catégorie ajoutée" });
    } finally { setCategoryLoading(false); }
  };

  const handleDeleteCategory = async (id: string, name: string) => {
    if (!vaultToken || !confirm(`Supprimer la catégorie "${name}" ?`)) return;
    const r = await vaultFetch(`/api/vault/categories/${id}`, { method: "DELETE" }, vaultToken);
    if (r.ok) { await loadCategories(); toast({ title: "Catégorie supprimée" }); }
  };

  const filteredEntries = entries.filter(e => {
    const q = searchQuery.toLowerCase();
    const matchSearch = !q
      || e.product_name.toLowerCase().includes(q)
      || (e.provider_name || "").toLowerCase().includes(q)
      || (e.login || "").toLowerCase().includes(q)
      || (e.category || "").toLowerCase().includes(q);
    const matchCat = filterCategory === "__all__" || e.category === filterCategory;
    const matchType = filterType === "__all__" || e.entry_type === filterType;
    return matchSearch && matchCat && matchType;
  });

  const subscriptionEntries = entries.filter(e => e.has_subscription);

  const monthlyTotal = subscriptionEntries
    .filter(e => e.status === "actif")
    .reduce((acc, e) => {
      const paid = parseFloat(e.amount_paid || "0");
      const monthly = parseFloat(e.price_monthly || "0");
      const annual = parseFloat(e.price_annual || "0");
      const effectiveMonthly = paid || monthly || (annual / 12);
      if (e.recurrence === "mensuel") return acc + effectiveMonthly;
      if (e.recurrence === "annuel") return acc + (paid || annual) / 12;
      return acc;
    }, 0);

  const renewalBadge = (dateStr?: string) => {
    if (!dateStr) return null;
    const diff = Math.ceil((new Date(dateStr).getTime() - Date.now()) / 86400000);
    if (diff <= 7) return <Badge className="bg-red-100 text-red-700 border-red-200 text-xs">🔴 J-{diff}</Badge>;
    if (diff <= 30) return <Badge className="bg-orange-100 text-orange-700 border-orange-200 text-xs">🟠 J-{diff}</Badge>;
    return null;
  };

  const CategorySelect = ({ value, onChange }: { value: string; onChange: (v: string) => void }) => (
    <Select value={value || "__none__"} onValueChange={v => onChange(v === "__none__" ? "" : v)}>
      <SelectTrigger data-testid="select-entry-category"><SelectValue placeholder="Choisir" /></SelectTrigger>
      <SelectContent>
        <SelectItem value="__none__">— Aucune —</SelectItem>
        {categories.map(c => <SelectItem key={c.id} value={c.name}>{c.name}</SelectItem>)}
      </SelectContent>
    </Select>
  );

  const EntryAvatar = ({ entry, size = "md" }: { entry: VaultEntry; size?: "sm" | "md" | "lg" }) => {
    const s = size === "sm" ? "w-8 h-8 text-xs" : size === "lg" ? "w-16 h-16 text-xl" : "w-12 h-12 text-sm";
    if (entry.logo_url) {
      return <img src={entry.logo_url} alt={entry.product_name} className={`${s} rounded-xl object-contain bg-white border border-border p-0.5 shrink-0`} />;
    }
    const initials = (entry.product_name || "?").slice(0, 2).toUpperCase();
    return (
      <div className={`${s} ${getAvatarBg(entry.product_name)} rounded-xl flex items-center justify-center text-white font-bold shrink-0`}>
        {initials}
      </div>
    );
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent
        className="max-w-6xl p-0 gap-0 rounded-lg border shadow-lg"
        style={{ height: '90vh', maxHeight: '90vh', overflow: 'hidden', display: 'flex', flexDirection: 'column' }}
      >
        <DialogTitle className="sr-only">Coffre-fort</DialogTitle>

        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b bg-card shrink-0">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-primary/10 rounded-lg">
              {isUnlocked ? <Unlock className="w-5 h-5 text-primary" /> : <Lock className="w-5 h-5 text-primary" />}
            </div>
            <div>
              <h2 className="text-lg font-bold">Coffre-fort</h2>
              <p className="text-xs text-muted-foreground">
                {isUnlocked ? `Session active — ${remainingMin} min restantes` : "Accès sécurisé"}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {isUnlocked && (
              <>
                <Button variant="outline" size="sm" onClick={() => setShowManageCategories(true)} data-testid="button-manage-categories">
                  <Tag className="w-4 h-4 mr-1" /> Catégories
                </Button>
                <Button variant="outline" size="sm" onClick={handleLock}>
                  <Lock className="w-4 h-4 mr-1" /> Verrouiller
                </Button>
              </>
            )}
            <Button variant="ghost" size="icon" onClick={onClose} data-testid="button-close-vault">
              <XIcon className="w-4 h-4" />
            </Button>
          </div>
        </div>

        {/* PIN Screen */}
        {!isUnlocked && (
          <div className="flex-1 flex items-center justify-center">
            <div className="w-full max-w-sm space-y-6 px-6">
              <div className="text-center space-y-1">
                <div className="mx-auto w-16 h-16 flex items-center justify-center rounded-full bg-primary/10 mb-4">
                  <ShieldCheck className="w-8 h-8 text-primary" />
                </div>
                <h3 className="text-xl font-semibold">
                  {setupMode ? "Configurer votre PIN" : "Entrer le PIN"}
                </h3>
                <p className="text-sm text-muted-foreground">
                  {setupMode ? "Choisissez un PIN à 6 chiffres pour sécuriser le coffre-fort." : "Saisissez votre PIN à 6 chiffres pour accéder au coffre."}
                </p>
              </div>
              <div className="flex gap-3 justify-center">
                {pinDigits.map((d, i) => (
                  <input
                    key={i}
                    ref={el => { pinInputRefs.current[i] = el; }}
                    type="password" inputMode="numeric" maxLength={1} value={d}
                    onChange={e => handlePinInput(i, e.target.value)}
                    onKeyDown={e => handlePinKeyDown(i, e)}
                    className="w-12 h-14 text-center text-2xl font-mono border-2 rounded-xl bg-background focus:outline-none focus:border-primary transition-colors"
                    data-testid={`pin-digit-${i}`} autoFocus={i === 0}
                  />
                ))}
              </div>
              {pinError && <p className="text-sm text-destructive text-center">{pinError}</p>}
              <Button className="w-full" onClick={() => handleVerifyPin(pinDigits.join(""))}
                disabled={pinDigits.some(d => d === "") || pinLoading} data-testid="button-verify-pin">
                {pinLoading ? "Vérification..." : setupMode ? "Configurer le PIN" : "Déverrouiller"}
              </Button>
            </div>
          </div>
        )}

        {/* Vault Content */}
        {isUnlocked && (
          <div style={{ flex: 1, minHeight: 0, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
            <Tabs defaultValue="acces" style={{ flex: 1, minHeight: 0, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
              <div className="px-6 pt-3 shrink-0">
                <TabsList className="w-full max-w-sm">
                  <TabsTrigger value="acces" className="flex-1" data-testid="tab-acces">Accès &amp; Outils</TabsTrigger>
                  <TabsTrigger value="abonnements" className="flex-1" data-testid="tab-abonnements">Abonnements</TabsTrigger>
                </TabsList>
              </div>

              {/* TAB 1 — Accès & Outils — Card Grid */}
              <TabsContent
                value="acces"
                className="mt-0"
                style={{ flex: 1, minHeight: 0, overflowY: 'auto', padding: '12px 24px 12px' }}
              >
                {/* Filters bar */}
                <div className="flex items-center gap-2 flex-wrap mb-2">
                  <div className="relative flex-1 min-w-48">
                    <Search className="absolute left-3 top-2.5 h-4 w-4 text-muted-foreground" />
                    <Input placeholder="Rechercher..." className="pl-9" value={searchQuery}
                      onChange={e => setSearchQuery(e.target.value)} data-testid="input-vault-search" />
                  </div>
                  <Select value={filterCategory} onValueChange={setFilterCategory}>
                    <SelectTrigger className="w-44" data-testid="select-vault-category">
                      <SelectValue placeholder="Catégorie" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__all__">Toutes catégories</SelectItem>
                      {categories.map(c => <SelectItem key={c.id} value={c.name}>{c.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  <Select value={filterType} onValueChange={setFilterType}>
                    <SelectTrigger className="w-40" data-testid="select-vault-type">
                      <SelectValue placeholder="Type" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__all__">Tous les types</SelectItem>
                      <SelectItem value="application">Application</SelectItem>
                      <SelectItem value="prestataire">Prestataire</SelectItem>
                    </SelectContent>
                  </Select>
                  <Button size="sm" onClick={openCreate} data-testid="button-add-entry">
                    <Plus className="w-4 h-4 mr-1" /> Ajouter
                  </Button>
                </div>

                {/* Card Grid */}
                <div>
                  {loading ? (
                    <div className="flex items-center justify-center h-40 text-muted-foreground">Chargement...</div>
                  ) : filteredEntries.length === 0 ? (
                    <div className="flex items-center justify-center h-40 text-muted-foreground">Aucune entrée trouvée</div>
                  ) : (
                    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2 pb-2">
                      {filteredEntries.map(entry => (
                        <div
                          key={entry.id}
                          data-testid={`vault-card-${entry.id}`}
                          className="group relative bg-card border border-border rounded-lg p-3 flex flex-col gap-2 hover:border-primary/40 hover:shadow-sm transition-all cursor-pointer"
                          onClick={() => { setDetailEntry(entry); setShowDetailDialog(true); }}
                        >
                          {/* Logo + Name */}
                          <div className="flex items-start gap-2">
                            <EntryAvatar entry={entry} size="sm" />
                            <div className="flex-1 min-w-0">
                              <p className="font-semibold text-xs truncate leading-tight">{entry.product_name}</p>
                              {entry.provider_name && (
                                <p className="text-[11px] text-muted-foreground truncate">{entry.provider_name}</p>
                              )}
                              <div className="flex gap-1 mt-1 flex-wrap">
                                {entry.category && (
                                  <Badge variant="secondary" className="text-[10px] px-1 py-0 h-4">{entry.category}</Badge>
                                )}
                                <Badge
                                  variant="outline"
                                  className={`text-[10px] px-1 py-0 h-4 ${entry.entry_type === "prestataire" ? "border-violet-400 text-violet-700" : "border-blue-400 text-blue-700"}`}
                                >
                                  {entry.entry_type === "prestataire" ? "Prest." : "App"}
                                </Badge>
                              </div>
                            </div>
                          </div>

                          {/* Footer */}
                          <div className="flex items-center justify-between pt-1.5 border-t border-border/60">
                            <div className="flex items-center gap-1.5">
                              <span className={`w-1.5 h-1.5 rounded-full ${STATUS_COLORS[entry.status || "actif"]}`} />
                              <span className="text-[11px] text-muted-foreground">{STATUS_LABELS[entry.status || "actif"]}</span>
                            </div>
                            <div className="flex items-center gap-0.5">
                              {entry.price_monthly && (
                                <span className="text-[11px] font-semibold text-foreground">{fmt(entry.price_monthly)}</span>
                              )}
                              {entry.site_url && (
                                <a href={entry.site_url} target="_blank" rel="noopener noreferrer"
                                  className="p-0.5 rounded hover:bg-secondary text-muted-foreground hover:text-primary transition-colors"
                                  onClick={e => e.stopPropagation()}>
                                  <Globe className="w-3 h-3" />
                                </a>
                              )}
                              <Button variant="ghost" size="icon" className="h-6 w-6 opacity-0 group-hover:opacity-100 transition-opacity"
                                onClick={e => { e.stopPropagation(); openEdit(entry); }} data-testid={`button-edit-${entry.id}`}>
                                <Pencil className="h-2.5 w-2.5" />
                              </Button>
                              <Button variant="ghost" size="icon" className="h-6 w-6 text-destructive hover:text-destructive opacity-0 group-hover:opacity-100 transition-opacity"
                                onClick={e => { e.stopPropagation(); handleDelete(entry.id); }} data-testid={`button-delete-${entry.id}`}>
                                <Trash2 className="h-2.5 w-2.5" />
                              </Button>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </TabsContent>

              {/* TAB 2 — Abonnements */}
              <TabsContent
                value="abonnements"
                className="mt-0"
                style={{ flex: 1, minHeight: 0, overflowY: 'auto', padding: '8px 24px 12px' }}
              >
                <div className="border rounded-lg">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Outil / Service</TableHead>
                        <TableHead>Catégorie</TableHead>
                        <TableHead>Forfait</TableHead>
                        <TableHead>Récurrence</TableHead>
                        <TableHead className="text-right">Prix mensuel</TableHead>
                        <TableHead className="text-right">Prix annuel</TableHead>
                        <TableHead className="text-right">Facturé</TableHead>
                        <TableHead>Souscription</TableHead>
                        <TableHead>Renouvellement</TableHead>
                        <TableHead>Statut</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {subscriptionEntries.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={10} className="text-center py-8 text-muted-foreground">
                            Aucun abonnement configuré. Activez "Souscription active" dans une fiche pour l'afficher ici.
                          </TableCell>
                        </TableRow>
                      ) : subscriptionEntries.map(entry => (
                        <TableRow key={entry.id} className="cursor-pointer hover:bg-secondary/40"
                          onClick={() => { setDetailEntry(entry); setShowDetailDialog(true); }}>
                          <TableCell>
                            <div className="flex items-center gap-2">
                              <EntryAvatar entry={entry} size="sm" />
                              <div>
                                <p className="font-medium text-sm">{entry.product_name}</p>
                                {entry.provider_name && <p className="text-xs text-muted-foreground">{entry.provider_name}</p>}
                              </div>
                            </div>
                          </TableCell>
                          <TableCell className="text-xs text-muted-foreground">{entry.category || "—"}</TableCell>
                          <TableCell className="text-sm">{entry.contract_name || "—"}</TableCell>
                          <TableCell>
                            {entry.recurrence ? (
                              <Badge variant="secondary" className="text-xs capitalize">
                                {RECURRENCE_LABELS[entry.recurrence] || entry.recurrence}
                              </Badge>
                            ) : "—"}
                          </TableCell>
                          <TableCell className="text-right font-mono text-sm">{fmt(entry.price_monthly)}</TableCell>
                          <TableCell className="text-right font-mono text-sm">{fmt(entry.price_annual)}</TableCell>
                          <TableCell className="text-right font-mono text-sm font-semibold">{fmt(entry.amount_paid)}</TableCell>
                          <TableCell className="text-sm">
                            {entry.subscription_date ? new Date(entry.subscription_date).toLocaleDateString("fr-FR") : "—"}
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center gap-2">
                              <span className="text-sm">{entry.renewal_date ? new Date(entry.renewal_date).toLocaleDateString("fr-FR") : "—"}</span>
                              {renewalBadge(entry.renewal_date)}
                            </div>
                          </TableCell>
                          <TableCell>
                            <Badge variant="outline" className="text-xs">
                              {STATUS_LABELS[entry.status || "actif"]}
                            </Badge>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
                <div className="shrink-0 flex justify-between items-center py-3 px-4 rounded-lg bg-secondary/40 border">
                  <span className="text-sm text-muted-foreground">
                    {subscriptionEntries.filter(e => e.status === "actif").length} abonnement(s) actif(s)
                  </span>
                  <div className="text-right">
                    <p className="text-xs text-muted-foreground">Coût mensuel estimé</p>
                    <p className="text-lg font-bold tabular-nums">{monthlyTotal.toFixed(2)} € HT/mois</p>
                  </div>
                </div>
              </TabsContent>
            </Tabs>
          </div>
        )}

        {/* Entry Detail Dialog */}
        <InnerDialog open={showDetailDialog} onOpenChange={setShowDetailDialog}>
          <InnerDialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
            <InnerDialogHeader>
              <InnerDialogTitle className="flex items-center gap-3">
                {detailEntry && <EntryAvatar entry={detailEntry} size="sm" />}
                <div>
                  <p className="font-bold">{detailEntry?.product_name}</p>
                  {detailEntry?.provider_name && <p className="text-sm font-normal text-muted-foreground">{detailEntry.provider_name}</p>}
                </div>
              </InnerDialogTitle>
            </InnerDialogHeader>
            {detailEntry && (
              <div className="space-y-4 py-2">
                <div className="flex gap-2">
                  {detailEntry.category && <Badge variant="secondary">{detailEntry.category}</Badge>}
                  <Badge variant="outline" className={detailEntry.entry_type === "prestataire" ? "border-violet-400 text-violet-700" : "border-blue-400 text-blue-700"}>
                    {detailEntry.entry_type === "prestataire" ? "Prestataire" : "Application"}
                  </Badge>
                  <Badge variant="outline">
                    <span className={`w-2 h-2 rounded-full mr-1 ${STATUS_COLORS[detailEntry.status || "actif"]}`} />
                    {STATUS_LABELS[detailEntry.status || "actif"]}
                  </Badge>
                </div>

                {detailEntry.description && (
                  <p className="text-sm text-muted-foreground">{detailEntry.description}</p>
                )}

                {detailEntry.site_url && (
                  <a href={detailEntry.site_url} target="_blank" rel="noopener noreferrer"
                    className="flex items-center gap-2 text-primary text-sm hover:underline">
                    <ExternalLink className="w-4 h-4" /> {detailEntry.site_url}
                  </a>
                )}

                {/* Credentials (hidden for prestataires) */}
                {detailEntry.entry_type !== "prestataire" && (detailEntry.login || detailEntry.password) && (
                  <div className="border rounded-lg p-3 space-y-2">
                    <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Identifiants</p>
                    {detailEntry.login && (
                      <div className="flex items-center justify-between">
                        <span className="text-sm font-mono">{detailEntry.login}</span>
                        <Button variant="ghost" size="icon" className="h-7 w-7"
                          onClick={() => copyText(detailEntry.login!, "Login copié")}>
                          <Copy className="h-3 w-3" />
                        </Button>
                      </div>
                    )}
                    {detailEntry.password && (
                      <div className="flex items-center justify-between">
                        <span className="text-sm font-mono">
                          {revealedPasswords.has(detailEntry.id) ? detailEntry.password : "••••••••••"}
                        </span>
                        <div className="flex gap-1">
                          <Button variant="ghost" size="icon" className="h-7 w-7"
                            onClick={() => toggleReveal(detailEntry.id)}>
                            {revealedPasswords.has(detailEntry.id) ? <EyeOff className="h-3 w-3" /> : <Eye className="h-3 w-3" />}
                          </Button>
                          <Button variant="ghost" size="icon" className="h-7 w-7"
                            onClick={() => copyText(detailEntry.password!, "Mot de passe copié")}>
                            <Copy className="h-3 w-3" />
                          </Button>
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* Financial */}
                {(detailEntry.contract_name || detailEntry.price_monthly || detailEntry.price_annual || detailEntry.amount_paid) && (
                  <div className="border rounded-lg p-3 space-y-2">
                    <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Contrat & Facturation</p>
                    <div className="grid grid-cols-2 gap-2 text-sm">
                      {detailEntry.contract_name && <><span className="text-muted-foreground">Forfait</span><span className="font-medium">{detailEntry.contract_name}</span></>}
                      {detailEntry.recurrence && <><span className="text-muted-foreground">Récurrence</span><span>{RECURRENCE_LABELS[detailEntry.recurrence] || detailEntry.recurrence}</span></>}
                      {detailEntry.price_monthly && <><span className="text-muted-foreground">Prix mensuel</span><span className="font-mono">{fmt(detailEntry.price_monthly)}</span></>}
                      {detailEntry.price_annual && <><span className="text-muted-foreground">Prix annuel</span><span className="font-mono">{fmt(detailEntry.price_annual)}</span></>}
                      {detailEntry.amount_paid && <><span className="text-muted-foreground">Montant facturé</span><span className="font-mono font-semibold">{fmt(detailEntry.amount_paid)}</span></>}
                      {detailEntry.subscription_date && <><span className="text-muted-foreground">Date souscription</span><span>{new Date(detailEntry.subscription_date).toLocaleDateString("fr-FR")}</span></>}
                    </div>
                  </div>
                )}

                {/* Contact */}
                {(detailEntry.commercial_contact || detailEntry.phone || detailEntry.email) && (
                  <div className="border rounded-lg p-3 space-y-2">
                    <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Contact</p>
                    <div className="space-y-1 text-sm">
                      {detailEntry.commercial_contact && (
                        <div className="flex items-center gap-2"><User className="w-3.5 h-3.5 text-muted-foreground" />{detailEntry.commercial_contact}</div>
                      )}
                      {detailEntry.phone && (
                        <div className="flex items-center gap-2"><Phone className="w-3.5 h-3.5 text-muted-foreground" />{detailEntry.phone}</div>
                      )}
                      {detailEntry.email && (
                        <div className="flex items-center gap-2"><Mail className="w-3.5 h-3.5 text-muted-foreground" />{detailEntry.email}</div>
                      )}
                    </div>
                  </div>
                )}

                {detailEntry.comment && (
                  <div className="text-sm text-muted-foreground bg-secondary/40 rounded-lg p-3">{detailEntry.comment}</div>
                )}
              </div>
            )}
            <div className="flex justify-between pt-2">
              <Button variant="outline" onClick={() => setShowDetailDialog(false)}>Fermer</Button>
              <Button onClick={() => { setShowDetailDialog(false); detailEntry && openEdit(detailEntry); }}>
                <Pencil className="w-4 h-4 mr-1" /> Modifier
              </Button>
            </div>
          </InnerDialogContent>
        </InnerDialog>

        {/* Entry Form Dialog */}
        <InnerDialog open={showFormDialog} onOpenChange={setShowFormDialog}>
          <InnerDialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <InnerDialogHeader>
              <InnerDialogTitle>{editingEntry ? "Modifier la fiche" : "Ajouter un outil / prestataire"}</InnerDialogTitle>
            </InnerDialogHeader>

            <div className="space-y-4 py-2">
              {/* Logo upload */}
              <div className="flex items-center gap-4">
                {formData.logoUrl ? (
                  <div className="relative">
                    <img src={formData.logoUrl} alt="Logo" className="w-16 h-16 rounded-xl object-contain border bg-white p-1" />
                    <Button variant="ghost" size="icon" className="absolute -top-1 -right-1 h-5 w-5 bg-background border rounded-full"
                      onClick={() => setFormData(p => ({ ...p, logoUrl: "" }))}>
                      <XIcon className="h-3 w-3" />
                    </Button>
                  </div>
                ) : (
                  <div className={`w-16 h-16 ${getAvatarBg(formData.productName || "?")} rounded-xl flex items-center justify-center text-white font-bold text-lg`}>
                    {(formData.productName || "?").slice(0, 2).toUpperCase()}
                  </div>
                )}
                <div className="space-y-1">
                  <Label className="text-xs text-muted-foreground">Logo (JPG, PNG, SVG)</Label>
                  <Button variant="outline" size="sm" onClick={() => logoInputRef.current?.click()}
                    disabled={logoUploading} data-testid="button-upload-logo">
                    <Upload className="w-3.5 h-3.5 mr-1" />
                    {logoUploading ? "Upload..." : "Choisir un logo"}
                  </Button>
                  <input ref={logoInputRef} type="file" accept="image/*" className="hidden"
                    onChange={e => e.target.files?.[0] && handleLogoUpload(e.target.files[0])} />
                </div>
              </div>

              {/* Type radio-like buttons */}
              <div className="space-y-1.5">
                <Label>Type *</Label>
                <div className="flex gap-2">
                  {[{ val: "application", label: "Application" }, { val: "prestataire", label: "Prestataire" }].map(opt => (
                    <button key={opt.val} type="button"
                      className={`flex-1 py-2 rounded-lg border text-sm font-medium transition-colors ${formData.entryType === opt.val ? "bg-primary text-primary-foreground border-primary" : "bg-background border-border hover:bg-secondary"}`}
                      onClick={() => setFormData(p => ({ ...p, entryType: opt.val }))}
                      data-testid={`button-type-${opt.val}`}>
                      {opt.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Core fields */}
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <Label>Nom du produit *</Label>
                  <Input value={formData.productName}
                    onChange={e => setFormData(p => ({ ...p, productName: e.target.value }))}
                    placeholder="Ex: Google Workspace" data-testid="input-site-name" />
                </div>
                <div className="space-y-1">
                  <Label>Prestataire</Label>
                  <Input value={formData.providerName}
                    onChange={e => setFormData(p => ({ ...p, providerName: e.target.value }))}
                    placeholder="Ex: Microsoft Advertising" data-testid="input-provider-name" />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <Label>Catégorie</Label>
                  <CategorySelect value={formData.category} onChange={v => setFormData(p => ({ ...p, category: v }))} />
                </div>
                <div className="space-y-1">
                  <Label>Statut</Label>
                  <Select value={formData.status || "actif"} onValueChange={v => setFormData(p => ({ ...p, status: v }))}>
                    <SelectTrigger data-testid="select-entry-status"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {STATUSES.map(s => <SelectItem key={s} value={s}>{STATUS_LABELS[s]}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="space-y-1">
                <Label>URL du site</Label>
                <Input value={formData.siteUrl}
                  onChange={e => setFormData(p => ({ ...p, siteUrl: e.target.value }))}
                  placeholder="https://..." data-testid="input-site-url" />
              </div>

              <div className="space-y-1">
                <Label>Description</Label>
                <Textarea value={formData.description}
                  onChange={e => setFormData(p => ({ ...p, description: e.target.value }))}
                  placeholder="Décrivez l'usage de cet outil..." rows={3} data-testid="input-description" />
              </div>

              {/* Credentials — hidden for prestataires */}
              {formData.entryType !== "prestataire" && (
                <div className="border-t pt-4 space-y-3">
                  <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Identifiants</p>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-1">
                      <Label>Login</Label>
                      <Input value={formData.login}
                        onChange={e => setFormData(p => ({ ...p, login: e.target.value }))}
                        placeholder="Email ou identifiant" data-testid="input-login" />
                    </div>
                    <div className="space-y-1">
                      <Label>{editingEntry ? "Nouveau mot de passe" : "Mot de passe"}</Label>
                      <Input type="password" value={formData.password}
                        onChange={e => setFormData(p => ({ ...p, password: e.target.value }))}
                        placeholder={editingEntry ? "Laisser vide = inchangé" : ""} data-testid="input-password" />
                    </div>
                  </div>
                </div>
              )}

              {/* Contract & Billing */}
              <div className="border-t pt-4 space-y-3">
                <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Contrat &amp; Facturation</p>

                <div className="flex items-center justify-between rounded-lg border px-4 py-3">
                  <div>
                    <p className="text-sm font-medium">Souscription active</p>
                    <p className="text-xs text-muted-foreground">Afficher dans l'onglet Abonnements</p>
                  </div>
                  <Switch checked={formData.hasSubscription}
                    onCheckedChange={v => setFormData(p => ({ ...p, hasSubscription: v }))}
                    data-testid="switch-has-subscription" />
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1">
                    <Label>Nom du forfait</Label>
                    <Input value={formData.contractName}
                      onChange={e => setFormData(p => ({ ...p, contractName: e.target.value }))}
                      placeholder="Ex: Pro, Core, Sur-mesure" data-testid="input-contract-name" />
                  </div>
                  <div className="space-y-1">
                    <Label>Récurrence</Label>
                    <Select value={formData.recurrence || "__none__"}
                      onValueChange={v => setFormData(p => ({ ...p, recurrence: v === "__none__" ? "" : v }))}>
                      <SelectTrigger data-testid="select-recurrence"><SelectValue placeholder="Choisir" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="__none__">— Aucune —</SelectItem>
                        {RECURRENCE_OPTIONS.map(r => <SelectItem key={r} value={r}>{RECURRENCE_LABELS[r]}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="grid grid-cols-3 gap-3">
                  <div className="space-y-1">
                    <Label className="flex items-center gap-1"><Euro className="w-3 h-3" /> Prix mensuel</Label>
                    <Input type="number" step="0.01" value={formData.priceMonthly}
                      onChange={e => setFormData(p => ({ ...p, priceMonthly: e.target.value }))}
                      placeholder="0.00" data-testid="input-price-monthly" />
                  </div>
                  <div className="space-y-1">
                    <Label className="flex items-center gap-1"><Euro className="w-3 h-3" /> Prix annuel</Label>
                    <Input type="number" step="0.01" value={formData.priceAnnual}
                      onChange={e => setFormData(p => ({ ...p, priceAnnual: e.target.value }))}
                      placeholder="0.00" data-testid="input-price-annual" />
                  </div>
                  <div className="space-y-1">
                    <Label className="flex items-center gap-1"><Euro className="w-3 h-3" /> Montant facturé</Label>
                    <Input type="number" step="0.01" value={formData.amountPaid}
                      onChange={e => setFormData(p => ({ ...p, amountPaid: e.target.value }))}
                      placeholder="0.00" data-testid="input-amount-paid" />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1">
                    <Label className="flex items-center gap-1"><Calendar className="w-3 h-3" /> Date de souscription</Label>
                    <Input type="date" value={formData.subscriptionDate}
                      onChange={e => setFormData(p => ({ ...p, subscriptionDate: e.target.value }))}
                      data-testid="input-subscription-date" />
                  </div>
                  <div className="space-y-1">
                    <Label className="flex items-center gap-1"><Calendar className="w-3 h-3" /> Date de renouvellement</Label>
                    <Input type="date" value={formData.renewalDate}
                      onChange={e => setFormData(p => ({ ...p, renewalDate: e.target.value }))}
                      data-testid="input-renewal-date" />
                  </div>
                </div>
              </div>

              {/* Contact */}
              <div className="border-t pt-4 space-y-3">
                <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Contact</p>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1">
                    <Label>Contact commercial</Label>
                    <Input value={formData.commercialContact}
                      onChange={e => setFormData(p => ({ ...p, commercialContact: e.target.value }))}
                      data-testid="input-commercial-contact" />
                  </div>
                  <div className="space-y-1">
                    <Label>Téléphone</Label>
                    <Input value={formData.phone}
                      onChange={e => setFormData(p => ({ ...p, phone: e.target.value }))}
                      data-testid="input-phone" />
                  </div>
                </div>
                <div className="space-y-1">
                  <Label>Email contact</Label>
                  <Input type="email" value={formData.email}
                    onChange={e => setFormData(p => ({ ...p, email: e.target.value }))}
                    data-testid="input-email" />
                </div>
              </div>

              {/* Comments */}
              <div className="space-y-1">
                <Label>Notes / Commentaires</Label>
                <Textarea value={formData.comment}
                  onChange={e => setFormData(p => ({ ...p, comment: e.target.value }))}
                  rows={2} data-testid="input-comment" />
              </div>
            </div>

            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => setShowFormDialog(false)}>Annuler</Button>
              <Button onClick={handleSave} disabled={formLoading || !formData.productName} data-testid="button-save-entry">
                {formLoading ? "Enregistrement..." : "Enregistrer"}
              </Button>
            </div>
          </InnerDialogContent>
        </InnerDialog>

        {/* Manage Categories Dialog */}
        <InnerDialog open={showManageCategories} onOpenChange={setShowManageCategories}>
          <InnerDialogContent className="max-w-md">
            <InnerDialogHeader>
              <InnerDialogTitle className="flex items-center gap-2">
                <Settings2 className="w-5 h-5" /> Gérer les catégories
              </InnerDialogTitle>
            </InnerDialogHeader>
            <div className="space-y-4 py-2">
              <div className="flex gap-2">
                <Input placeholder="Nom de la catégorie..." value={newCategoryName}
                  onChange={e => setNewCategoryName(e.target.value)}
                  onKeyDown={e => e.key === "Enter" && handleAddCategory()}
                  data-testid="input-new-category" />
                <Button onClick={handleAddCategory} disabled={categoryLoading || !newCategoryName.trim()}
                  data-testid="button-add-category">
                  <Plus className="w-4 h-4 mr-1" /> Ajouter
                </Button>
              </div>
              <div className="border rounded-lg overflow-hidden">
                {categories.length === 0 ? (
                  <p className="text-sm text-muted-foreground text-center py-6">Aucune catégorie</p>
                ) : (
                  <div className="divide-y max-h-80 overflow-y-auto">
                    {categories.map(cat => (
                      <div key={cat.id} className="flex items-center justify-between px-4 py-2.5 hover:bg-secondary/40 transition-colors"
                        data-testid={`category-row-${cat.id}`}>
                        <span className="text-sm font-medium">{cat.name}</span>
                        <Button variant="ghost" size="icon" className="h-7 w-7 text-muted-foreground hover:text-destructive hover:bg-destructive/10"
                          onClick={() => handleDeleteCategory(cat.id, cat.name)}
                          data-testid={`button-delete-category-${cat.id}`}>
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <p className="text-xs text-muted-foreground">
                {categories.length} catégorie{categories.length > 1 ? "s" : ""} — La suppression ne supprime pas les entrées associées.
              </p>
            </div>
            <div className="flex justify-end pt-2">
              <Button onClick={() => setShowManageCategories(false)}>Fermer</Button>
            </div>
          </InnerDialogContent>
        </InnerDialog>
      </DialogContent>
    </Dialog>
  );
}
